/**
 * One way to render an amount, so two screens cannot disagree about the units.
 *
 * `Loan.loanAmount` is a figure in DOLLARS. Several surfaces rendered it as
 * `` `$${amount.toFixed(1)}M` `` — appending the "M" without dividing — so a
 * $26.4M loan printed as "$26400000.0M" and a $407M book as "$407131007.0M",
 * which is four hundred trillion dollars on a client-facing screen. The unit
 * suffix has to be earned by the division that justifies it, and that is the
 * only thing this module does.
 */

/** An amount in dollars, at the magnitude a reader actually thinks in. */
export function formatUsd(amount: number): string {
  if (!Number.isFinite(amount)) return '—'
  // The sign leads the currency symbol — "-$26.4M", not "$-26.4M" — so the
  // magnitude is formatted from the absolute value and the sign put back.
  const sign = amount < 0 ? '-' : ''
  const magnitude = Math.abs(amount)
  if (magnitude >= 1_000_000_000) return `${sign}$${(magnitude / 1_000_000_000).toFixed(1)}B`
  if (magnitude >= 1_000_000) return `${sign}$${(magnitude / 1_000_000).toFixed(1)}M`
  if (magnitude >= 1_000) return `${sign}$${Math.round(magnitude / 1_000)}K`
  return `${sign}$${Math.round(magnitude)}`
}

/** The same, for a value that may not be recorded at all. */
export function formatUsdOrDash(amount: number | null | undefined): string {
  return amount == null ? '—' : formatUsd(amount)
}

/**
 * Dollars as a figure in millions, exact to $10,000 and rounded half up:
 * 17,250,000 → "17.25", 41,500,000 → "41.5", 24,000,000 → "24".
 *
 * A requested amount printed to one decimal read $17.25M as "$17.3M" and
 * $41.25M as "$41.3M" — overstating the ask by $50,000 on every screen the
 * desk compares to the dollar against Backshop (stress test OVR-26).
 */
export function millionsFigure(amount: number): string {
  // Counted in whole $10,000s first, so the rounding is decided on an integer
  // and not on a binary fraction ("17.255".toFixed(2) is "17.25").
  const tenThousands = Math.round(Math.abs(amount) / 10_000)
  const text = String(tenThousands / 100)
  return amount < 0 && tenThousands !== 0 ? `-${text}` : text
}
